import {
  Box,
  Typography,
  Button,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  EditOutlined,
  PermIdentity,
  EmailOutlined,
  PhoneOutlined,
  ChangeCircleOutlined,
} from "@mui/icons-material";
import { PropertyDetailsProps, PropertyDetailsData } from "./types";
import { useEffect, useState } from "react";
import { propertyDetailsTypes } from "../../../../utils/Constant";
import { mapPropertyRecordToOptionalDetails } from "../../../../utils/PropertyDetailsMapper";

const labelSx = {
  fontSize: 12,
  color: "rgba(0, 0, 0, 0.6)",
  mb: 0.5,
};

const valueSx = {
  fontWeight: 500,
  fontSize: 16,
  color: "rgba(0, 0, 0, 0.87)",
};

export default function PropertyDetails({
  propertyDetails,
  onEdit,
  onChangeProperty,
}: PropertyDetailsProps) {
  const [optionalPropertyDetails, setOptionalPropertyDetails] = useState<any[]>(
    []
  );

  useEffect(() => {
    if (propertyDetails) {
      const labelByType: Record<string, string> = Object.fromEntries(
        propertyDetailsTypes.map((type) => [type.value, type.label])
      );
      const mappedPropertyDetails = mapPropertyRecordToOptionalDetails(
        propertyDetails
      ).map((row) => ({
        key: row.type,
        label: labelByType[row.type] ?? row.type,
        value: row.description,
      }));
      setOptionalPropertyDetails(mappedPropertyDetails);
    }
  }, [propertyDetails]);

  const formatAddress = (property: PropertyDetailsData): string => {
    const parts = [
      property.street,
      property.address2 && property.address2 !== property.street
        ? property.address2
        : null,
      `${property.city}, ${property.state} ${property.zip}`,
      property.country,
    ].filter(Boolean);
    return parts.join("\n");
  };

  const formatPhone = (phone: string): string => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }
    return phone;
  };

  const sortedContacts = [...(propertyDetails?.contacts || [])].sort(
    (a: any, b: any) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      return 0;
    }
  );
  const contact1 = sortedContacts[0];
  const contact2 = sortedContacts[1];

  const renderContact = (contact: any) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
        <PermIdentity sx={{ color: "rgba(0, 0, 0, 0.54)", width: 20, height: 20 }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {contact.name}
        </Typography>
        {contact.isPrimary && (
          <Chip
            label="Primary"
            size="small"
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              fontWeight: 500,
              fontSize: "0.7rem",
              height: 20,
            }}
          />
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <EmailOutlined sx={{ color: "rgba(0, 0, 0, 0.54)", width: 20, height: 20 }} />
        <Typography variant="body2" sx={{ color: "rgba(0, 0, 0, 0.87)" }}>
          {contact.email}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <PhoneOutlined sx={{ color: "rgba(0, 0, 0, 0.54)", width: 20, height: 20 }} />
        <Typography variant="body2" sx={{ color: "rgba(0, 0, 0, 0.87)" }}>
          {formatPhone(contact.phone)}
          {contact.phoneType ? ` (${contact.phoneType})` : ""}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Header: Property title + EDIT / CHANGE PROPERTY */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 400 }}>
          Property
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<EditOutlined />}
            onClick={onEdit}
            sx={{
              color: "primary.main",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            EDIT
          </Button>
          <Button
            startIcon={<ChangeCircleOutlined />}
            onClick={onChangeProperty}
            sx={{
              color: "primary.main",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            CHANGE PROPERTY
          </Button>
        </Box>
      </Box>

      {/* Two-column: Loan # & Address | Property Name & Avatars */}
      <Grid container spacing={3} sx={{ width: "100%", mb: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography sx={labelSx}>Loan #</Typography>
          <Typography sx={valueSx}>
            {propertyDetails.loanNumber || "-"}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography sx={labelSx}>Address</Typography>
            <Typography sx={{ ...valueSx, fontWeight: 400, whiteSpace: "pre-line" }}>
              {formatAddress(propertyDetails) || "-"}
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography sx={labelSx}>Property Name</Typography>
          <Typography sx={{ ...valueSx, fontWeight: 400 }}>
            {propertyDetails.propertyName || "-"}
          </Typography>
        </Grid>
      </Grid>

      {/* Property Contact section */}
      <Typography
        variant="body2"
        sx={{
          fontSize: 12,
          color: "rgba(0, 0, 0, 0.6)",
          mb: 1.5,
          mt: 2,
        }}
      >
        Property Contact
      </Typography>
      <Grid container spacing={3} sx={{ width: "100%" }}>
        {contact1 && (
          <Grid size={{ xs: 12, md: 6 }}>
            {renderContact(contact1)}
          </Grid>
        )}
        {contact2 && (
          <Grid size={{ xs: 12, md: 6 }}>
            {renderContact(contact2)}
          </Grid>
        )}
        {sortedContacts.length > 2 &&
          sortedContacts.slice(2).map((contact: any, index: number) => (
            <Grid key={index} size={{ xs: 12, md: 6 }}>
              {renderContact(contact)}
            </Grid>
          ))}
      </Grid>
      {optionalPropertyDetails.length > 0 && (
          <Box mt={2}>
            <Typography
              sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)", mb: 1 }}
            >
              Optional Property Details
            </Typography>
            <Box display={"flex"}>
              <Typography sx={{ flex: 1 }}>
                <b>Type</b>
              </Typography>
              <Typography sx={{ flex: 3 }}>
                <b>Description</b>
              </Typography>
            </Box>
            {optionalPropertyDetails.map((details) => (
              <Box display={"flex"} mt={0.5}>
                <Typography sx={{ flex: 1 }}>{details?.label}</Typography>
                <Typography sx={{ flex: 3 }}>
                  {details?.key === "operationsAndMaintenanceRequired"
                    ? [details?.value === true ? "True" : "False"]
                    : String(details?.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
    </Box>
  );
}
